import { ReactNode, useState } from "react";
import { FileText, Phone, ExternalLink, MapPin, ListChecks, ScrollText } from "lucide-react";
import { AvatarProps, ChatMessage } from "../../../../views/crm-pages/conversation/types";
import { getMessageStatusIcon, Icons } from "../../../../views/crm-pages/conversation/constant";
import { formatTime } from "../../../../utils/function";
import { InteractiveOptionsModal, InteractivePreview, InteractiveListRow } from "./interactive-options-modal";
import { CatalogTemplate, isTemplateMessage, resolveTemplateOptions, resolveTemplateText } from "./template-display";

export const Avatar: React.FC<AvatarProps> = ({ src, initials, color, size = 38 }) => {
  if (src) return (
    <img
      src={src}
      alt={initials}
      width={size}
      height={size}
      className="rounded-full object-cover flex-shrink-0 border-2 border-white dark:border-gray-800"
      style={{ width: size, height: size }}
    />
  );
  return (
    <div
      style={{
        width: size,
        height: size,
        backgroundColor: color || "#6366f1"
      }}
      className="rounded-full flex items-center justify-center text-white font-bold flex-shrink-0"
    >
      <span style={{ fontSize: size * 0.35 }}>{initials}</span>
    </div>
  );
};

export const Badge: React.FC<{ count: number | null }> = ({ count }) => {
  if (!count) return null;
  return (
    <span className="bg-[#25D366] text-white rounded-full text-xs font-bold px-[7px] py-[2px] min-w-[20px] text-center">
      {count}
    </span>
  );
};

 interface SectionHeaderProps { title: string; defaultOpen?: boolean; children: ReactNode; }
export const SectionHeader: React.FC<SectionHeaderProps> = ({ title, defaultOpen = true, children }) => {
  const [open, setOpen] = useState<boolean>(defaultOpen);
  return (
    <div className="border-b border-slate-100 dark:border-gray-800">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex justify-between items-center p-3 px-4 bg-transparent border-none cursor-pointer font-bold text-xs text-slate-800 dark:text-gray-100"
      >
        {title}
        <span className={`transition-transform duration-200 text-slate-400 dark:text-gray-500 ${open ? "rotate-180" : ""}`}>
          <Icons.ChevronDown size={16} />
        </span>
      </button>
      {open && <div className="px-4 pb-3">{children}</div>}
    </div>
  );
};

// Splits message text on URLs (http(s):// or bare www.) and renders each as a
// real clickable link — plain text otherwise never gets an anchor tag.
const URL_PATTERN = /((?:https?:\/\/|www\.)[^\s]+)/gi;

const linkifyMessageBody = (text: string, linkClassName: string): React.ReactNode[] =>
  text?.split(URL_PATTERN).map((part, i) =>
    /^(?:https?:\/\/|www\.)/i.test(part) ? (
      <a
        key={i}
        href={part.startsWith("http") ? part : `https://${part}`}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
        className={linkClassName}
      >
        {part}
      </a>
    ) : (
      part
    )
  );

// Prefers sender_type over the older `direction` field for alignment/color,
// since "outbound" alone can't distinguish a bot reply from a human agent's.
const isOutboundMessage = (msg: ChatMessage): boolean =>
  msg.sender_type ? msg.sender_type !== "customer" : msg.direction === "outbound";

const isBotMessage = (msg: ChatMessage): boolean => msg.sender_type === "bot";

// Messages carry a `file` object when there's an attachment (seen null on
// text messages so far — real shape once populated is still TBD). Check the
// likely nested URL property names, plus a couple of legacy flat-field
// candidates, and fall back to `body` itself when it's already a URL.
// Also supports `_localPreview` for optimistic rendering of local uploads.
const resolveMediaUrl = (msg: ChatMessage): string | null => {
  // Check for local preview first (used during optimistic rendering)
  if ((msg as any)._localPreview) {
    return (msg as any)._localPreview;
  }

  const file = msg.file as Record<string, any> | string | null | undefined;
  const candidate =
    (typeof file === "string" ? file : null) ||
    file?.url ||
    file?.file_url ||
    file?.public_url ||
    file?.download_url ||
    file?.path ||
    file?.href ||
    (msg as any).media_url ||
    (msg as any).file_url ||
    (msg as any).attachment_url ||
    (msg.type !== "text" && /^https?:\/\//i.test(msg.body || "") ? msg.body : null);
  return candidate || null;
};

// WhatsApp doesn't transcode webm/csv/plain-text, so those are dispatched to
// the recipient with `type: "document"` (see conversation-analytics-export-
// and-media-api.md) — that's correct for what WhatsApp shows them, but our
// own inbox has the real file and should still let the agent play audio
// inline rather than just offering a bare download link.
const getFileMimeType = (msg: ChatMessage): string | null => {
  // Check the client-set field first — it survives the post-send merge even
  // when the server's `file` object replaces ours wholesale without echoing
  // mime_type back (observed in practice).
  if (msg._mimeType) return msg._mimeType;
  const file = msg.file as Record<string, any> | string | null | undefined;
  if (!file || typeof file === "string") return null;
  return file.mime_type || file.content_type || file.mimeType || null;
};

const MediaContent: React.FC<{ msg: ChatMessage }> = ({ msg }) => {
  const url = resolveMediaUrl(msg);

  if (!url) {
    return <span className="text-xs italic text-slate-400 dark:text-gray-500">{msg.type} unavailable</span>;
  }

  if (msg.type === "image") {
    return (
      <a href={url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
        <img src={url} alt="Sent attachment" className="max-w-full max-h-64 rounded-lg object-cover" />
      </a>
    );
  }

  if (msg.type === "video") {
    return <video src={url} controls className="max-w-full max-h-64 rounded-lg" />;
  }

  if (msg.type === "audio") {
    return <audio src={url} controls className="max-w-[240px]" />;
  }

  if (msg.type === "document") {
    const mimeType = getFileMimeType(msg);
    if (mimeType?.startsWith("audio/")) {
      return <audio src={url} controls className="max-w-[240px]" />;
    }
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
        className="flex items-center gap-2 text-indigo-600 dark:text-indigo-400 underline text-sm"
      >
        <FileText size={14} /> Document
      </a>
    );
  }

  return null;
};

// Only these types actually carry an attachment to render via MediaContent.
// Non-media types like "template" or "interactive" still just have a text
// `body` — treating them as media showed a bogus "template unavailable" badge.
const MEDIA_MESSAGE_TYPES = ["image", "video", "audio", "document"];

// Per itemsecure-interactive-inbox-ui.md: an inbound `interactive` message's
// `body` is the raw selected row/button ID (e.g. "F_ROLE:HOMEOWNER") — the
// human-readable choice is `message_metadata.interactive_reply_title`, which
// should be shown instead, falling back to the ID only if it's missing.
const getInboundInteractiveLabel = (msg: ChatMessage): string => {
  const metadata = msg.message_metadata as any;
  return metadata?.interactive_reply_title || msg.body;
};

const humanizeKey = (s: string) => s.replace(/[_-]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

// A WhatsApp Flow *submission* (inbound, `interactive_type: "nfm_reply"`) —
// confirmed live from a real message: `body` is just the generic "Flow
// submitted", and the actual answers the customer gave live in
// `message_metadata.flow_response` as a flat object whose keys are entirely
// flow-specific (a site-survey flow's fields look nothing like a booking
// flow's) — rendered as a dynamic label/value list rather than a fixed
// schema, since there's no shared shape across flows to assume. `flow_token`
// is excluded — an internal WhatsApp identifier, not something the customer
// answered.
const getFlowSubmission = (msg: ChatMessage): { label: string; value: string }[] | null => {
  const metadata = msg.message_metadata as any;
  if (metadata?.interactive_type !== "nfm_reply" || !metadata?.flow_response || typeof metadata.flow_response !== "object") return null;
  return Object.entries(metadata.flow_response)
    .filter(([key]) => key !== "flow_token")
    .map(([key, value]) => ({ label: humanizeKey(key), value: String(value) }));
};

const normalizeListRow = (row: any): InteractiveListRow => ({
  id: row?.id,
  title: row?.title || row?.label || row?.id || "",
  description: row?.description,
});

// The outbound bot message that *presented* the options (list/buttons/flow/
// location request) — read-only preview data per the same doc: "use
// bot_response.messages[0].sections when present... Fall back to rows for
// older bot responses." Field names beyond that aren't pinned down yet, so
// this reads defensively (flat on messages[0], or nested under a same-named
// key) rather than assuming one exact shape — returns null for a plain text
// bot message or one with no bot_response at all.
const getBotInteractivePreview = (msg: ChatMessage): InteractivePreview | null => {
  const metadata = msg.message_metadata as any;
  const botMsg = metadata?.bot_response?.messages?.[0];
  if (!botMsg) return null;

  const kind: string | undefined = botMsg.type;

  if (kind === "list" || (!kind && (botMsg.sections || botMsg.rows))) {
    const rawSections = botMsg.sections || botMsg.list?.sections;
    const rawRows = botMsg.rows || botMsg.list?.rows;
    const sections = Array.isArray(rawSections) && rawSections.length
      ? rawSections.map((s: any) => ({ title: s?.title, rows: (s?.rows || []).map(normalizeListRow) }))
      : Array.isArray(rawRows)
        ? [{ rows: rawRows.map(normalizeListRow) }]
        : [];
    return { kind: "list", headerText: botMsg.button || botMsg.header || botMsg.list?.button, bodyText: botMsg.body || msg.body, sections };
  }

  if (kind === "buttons" || botMsg.buttons) {
    const buttons = (botMsg.buttons || []).map((b: any) => (typeof b === "string" ? b : b?.title || b?.text || b?.id || "")).filter(Boolean);
    return { kind: "buttons", bodyText: botMsg.body || msg.body, buttons };
  }

  if (kind === "flow" || botMsg.flow) {
    const flow = botMsg.flow || {};
    return { kind: "flow", bodyText: botMsg.body || msg.body, ctaText: botMsg.cta_text || botMsg.button_text || flow.cta || flow.button_text || "Open form" };
  }

  if (kind === "location_request" || botMsg.location_request) {
    const loc = botMsg.location_request || {};
    return { kind: "location_request", bodyText: botMsg.body || msg.body, ctaText: loc.text || loc.body || "Share location" };
  }

  return null;
};

// Helper functions for RCS template rendering (handle both API and local field names)
const getRcsText = (comp: any): string | null => comp?.content || comp?.text || null;
const getRcsMediaUrl = (comp: any): string | null => comp?.mediaUrl || comp?.media_url || null;
const getRcsButtons = (comp: any): any[] => comp?.buttons || comp?.actions || [];
const getRcsReplies = (comp: any): string[] => {
  const replies = comp?.replies || [];
  if (replies.length === 0) return [];
  return replies.map((r: any) => typeof r === 'string' ? r : r?.text || '').filter(Boolean);
};
const getButtonActionType = (btn: any): string => {
  if (btn.action) return btn.action;
  const typeMap: Record<string, string> = {
    'PHONE_NUMBER': 'dial',
    'URL': 'open_url',
    'LOCATION': 'location',
    'CALENDAR': 'calendar',
  };
  return typeMap[btn.type] || 'open_url';
};

// RCS Template Content renderer for chat messages
const RcsTemplateContent: React.FC<{ msg: ChatMessage }> = ({ msg }) => {
  const metadata = msg.message_metadata as any;
  const components = metadata?.rcs?.components || [];
  const variables = metadata?.rcs?.variables || {};

  if (components.length === 0) {
    return <span className="text-xs italic text-slate-400 dark:text-gray-500">Template message</span>;
  }

  // Replace variables in text
  const replaceVars = (text: string): string => {
    if (!text) return text;
    return text.replace(/\{\{(\w+)\}\}/g, (match, key) => variables[key] || match);
  };

  return (
    <div className="flex flex-col gap-2">
      {components.map((comp: any, idx: number) => {
        const type = comp.type;

        if (type === 'text') {
          const text = getRcsText(comp);
          return text ? (
            <p key={idx} className="text-sm whitespace-pre-wrap">
              {replaceVars(text)}
            </p>
          ) : null;
        }

        if (type === 'image') {
          const url = getRcsMediaUrl(comp);
          return url ? (
            <a key={idx} href={url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
              <img src={url} alt="" className="max-w-full max-h-48 rounded-lg object-cover" />
            </a>
          ) : (
            <div key={idx} className="w-full h-24 bg-slate-100 dark:bg-gray-800 rounded-lg flex items-center justify-center">
              <span className="text-xs text-slate-400 dark:text-gray-500">Image</span>
            </div>
          );
        }

        if (type === 'rich_card') {
          const mediaUrl = getRcsMediaUrl(comp);
          const buttons = getRcsButtons(comp);
          return (
            <div key={idx} className="border border-slate-200 dark:border-gray-700 rounded-lg overflow-hidden bg-white dark:bg-gray-800">
              {mediaUrl && (
                <img src={mediaUrl} alt="" className="w-full h-28 object-cover" />
              )}
              <div className="p-2">
                {comp.title && <p className="text-sm font-semibold">{replaceVars(comp.title)}</p>}
                {comp.description && <p className="text-xs text-slate-500 dark:text-gray-400 mt-0.5">{replaceVars(comp.description)}</p>}
              </div>
              {buttons.length > 0 && (
                <div className="border-t border-slate-100 dark:border-gray-700 p-1.5 flex gap-1">
                  {buttons.slice(0, 2).map((btn: any, i: number) => {
                    const actionType = getButtonActionType(btn);
                    return (
                      <span key={i} className="flex-1 flex items-center justify-center gap-1 px-2 py-1 bg-blue-50 dark:bg-blue-500/15 rounded text-[10px] font-medium text-blue-600 dark:text-blue-400">
                        {actionType === 'dial' && <Phone size={10} />}
                        {actionType === 'open_url' && <ExternalLink size={10} />}
                        {btn.text || 'Button'}
                      </span>
                    );
                  })}
                </div>
              )}
            </div>
          );
        }

        if (type === 'carousel') {
          const cards = comp.cards || [];
          return cards.length > 0 ? (
            <div key={idx} className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
              {cards.map((card: any, cardIdx: number) => {
                const cardMediaUrl = getRcsMediaUrl(card);
                const cardButtons = getRcsButtons(card);
                return (
                  <div key={cardIdx} className="flex-shrink-0 w-36 border border-slate-200 dark:border-gray-700 rounded-lg overflow-hidden bg-white dark:bg-gray-800">
                    {cardMediaUrl ? (
                      <img src={cardMediaUrl} alt="" className="w-full h-20 object-cover" />
                    ) : (
                      <div className="w-full h-20 bg-slate-100 dark:bg-gray-700 flex items-center justify-center">
                        <span className="text-[10px] text-slate-400 dark:text-gray-500">Image</span>
                      </div>
                    )}
                    <div className="p-1.5">
                      {card.title && <p className="text-[11px] font-semibold truncate">{replaceVars(card.title)}</p>}
                      {card.description && <p className="text-[10px] text-slate-500 dark:text-gray-400 truncate">{replaceVars(card.description)}</p>}
                    </div>
                    {cardButtons.length > 0 && (
                      <div className="border-t border-slate-100 dark:border-gray-700 p-1">
                        {cardButtons.slice(0, 1).map((btn: any, i: number) => {
                          const actionType = getButtonActionType(btn);
                          return (
                            <span key={i} className="w-full flex items-center justify-center gap-0.5 px-1 py-0.5 bg-blue-50 dark:bg-blue-500/15 rounded text-[9px] font-medium text-blue-600 dark:text-blue-400">
                              {actionType === 'dial' && <Phone size={8} />}
                              {actionType === 'open_url' && <ExternalLink size={8} />}
                              {btn.text || 'Button'}
                            </span>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : null;
        }

        if (type === 'suggested_replies') {
          const replies = getRcsReplies(comp);
          return replies.length > 0 ? (
            <div key={idx} className="flex flex-wrap gap-1">
              {replies.map((reply, i) => (
                <span key={i} className="px-2 py-1 bg-slate-100 dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded-full text-[10px] text-slate-600 dark:text-gray-300">
                  {reply}
                </span>
              ))}
            </div>
          ) : null;
        }

        if (type === 'call_to_action' || type === 'cta_buttons') {
          const buttons = getRcsButtons(comp);
          return buttons.length > 0 ? (
            <div key={idx} className="flex gap-1">
              {buttons.map((btn: any, i: number) => {
                const actionType = getButtonActionType(btn);
                return (
                  <span key={i} className="flex items-center gap-1 px-2 py-1.5 bg-blue-500 rounded-lg text-[10px] font-medium text-white">
                    {actionType === 'dial' && <Phone size={10} />}
                    {actionType === 'open_url' && <ExternalLink size={10} />}
                    {actionType === 'location' && <MapPin size={10} />}
                    {btn.text || 'Action'}
                  </span>
                );
              })}
            </div>
          ) : null;
        }

        if (type === 'location') {
          const label = comp.locationLabel || comp.label || 'Location';
          return (
            <div key={idx} className="flex items-center gap-2 p-2 bg-slate-50 dark:bg-gray-800 rounded-lg">
              <MapPin size={16} className="text-slate-400 dark:text-gray-500" />
              <span className="text-xs text-slate-600 dark:text-gray-300">{label}</span>
            </div>
          );
        }

        return null;
      })}
    </div>
  );
};

// Check if message is an RCS template
const isRcsTemplateMessage = (msg: ChatMessage): boolean => {
  return msg.type === 'template' && !!(msg.message_metadata as any)?.rcs?.components;
};

export const MessageComponent: React.FC<{ msg: ChatMessage; onOpenForm?: () => void; templates?: CatalogTemplate[] }> = ({ msg, onOpenForm, templates = [] }) => {
  const [showOptions, setShowOptions] = useState(false);
  const isOut = isOutboundMessage(msg);
  const isMedia = MEDIA_MESSAGE_TYPES.includes(msg.type);
  const isRcsTemplate = isRcsTemplateMessage(msg);
  const isInteractive = msg.type === "interactive";
  // Inbound: the customer's own tap — show the chosen option's title, not the
  // raw list/button ID. Outbound: the bot's own list/buttons/flow/location
  // request message — a "View options" opens a read-only preview of what it
  // offered (see interactive-options-modal.tsx); null for anything else,
  // including a plain-text bot message with type "interactive".
  const botPreview = isInteractive && isOut ? getBotInteractivePreview(msg) : null;
  const flowSubmission = isInteractive && !isOut ? getFlowSubmission(msg) : null;
  const [showSubmission, setShowSubmission] = useState(false);
  // WhatsApp templates resolve their full text the same way mobile does
  // (see template-display.ts) instead of trusting `body` as-is.
  const isTemplate = !isRcsTemplate && isTemplateMessage(msg);
  const templateOptions = isTemplate ? resolveTemplateOptions(msg, templates) : null;
  const [showTemplateOptions, setShowTemplateOptions] = useState(false);
  const bodyText = isTemplate ? resolveTemplateText(msg, templates) : isInteractive && !isOut ? getInboundInteractiveLabel(msg) : msg.body;
  const displayTime = msg.sent_at ? formatTime(msg.sent_at) : formatTime(msg.created_at);
  const statusIcon = getMessageStatusIcon(msg.status, msg.direction);

  return (
    <div className={`flex ${isOut ? "justify-end" : "justify-start"} my-1 mx-4`}>
      <div className={`max-w-[72%] px-3.5 py-2.5 text-sm text-slate-800 leading-relaxed shadow-sm whitespace-pre-wrap ${isOut
        ? "bg-[#dcf8c6] dark:bg-emerald-900/40 dark:text-gray-100 rounded-tl-[4px] rounded-tr-[18px] rounded-br-[18px] rounded-bl-[18px]"
        : "bg-white border border-[#e8ecf4] dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100 rounded-tl-[18px] rounded-tr-[4px] rounded-br-[18px] rounded-bl-[18px]"
        }`}>
        {isOut && isBotMessage(msg) && (
          <span className="flex items-center gap-1 text-[10px] font-semibold text-indigo-500 dark:text-indigo-400 mb-1">
            <Icons.Bot /> Bot
          </span>
        )}

        {isTemplate && (
          <span className="flex items-center gap-1 text-[10px] font-semibold text-violet-600 dark:text-violet-400 mb-1">
            <ScrollText size={11} /> Template
          </span>
        )}

        {/* RCS Template Message */}
        {isRcsTemplate && <RcsTemplateContent msg={msg} />}

        {/* Media content (image, video, audio, document) */}
        {!isRcsTemplate && isMedia && <div className={msg.body ? "mb-1.5" : ""}><MediaContent msg={msg} /></div>}

        {/* Text body (inbound interactive taps show the chosen option's title here) */}
        {!isRcsTemplate && (!isMedia || msg.body) && (
          <span className="break-words">{linkifyMessageBody(bodyText, "underline text-blue-600 dark:text-blue-400 break-all")}</span>
        )}

        {/* Outbound bot list/buttons/flow/location-request — read-only
            preview. For a Flow message specifically, the modal's own "Open
            form" button (see interactive-options-modal.tsx) goes on to open
            the real Form Preview panel — pre-filled from this conversation's
            own contact, still static/non-submittable (form-preview-panel.tsx) —
            closing this popup first, same two-step tap as the reference. */}
        {botPreview && (
          <>
            <button
              onClick={() => setShowOptions(true)}
              className="flex items-center gap-1.5 mt-1.5 text-xs font-semibold text-indigo-600 dark:text-indigo-400 underline bg-transparent border-none p-0 cursor-pointer"
            >
              <ListChecks size={13} /> View options
            </button>
            <InteractiveOptionsModal
              isOpen={showOptions}
              preview={botPreview}
              onClose={() => setShowOptions(false)}
              onOpenForm={
                botPreview.kind === "flow" && onOpenForm
                  ? () => {
                      setShowOptions(false);
                      onOpenForm();
                    }
                  : undefined
              }
            />
          </>
        )}

        {templateOptions && (
          <>
            <button
              onClick={() => setShowTemplateOptions(true)}
              className="flex items-center gap-1.5 mt-1.5 text-xs font-semibold text-indigo-600 dark:text-indigo-400 underline bg-transparent border-none p-0 cursor-pointer"
              aria-label="View template options"
            >
              <ListChecks size={13} /> View options
            </button>
            <InteractiveOptionsModal
              isOpen={showTemplateOptions}
              preview={{
                kind: "buttons",
                title: "Template options",
                caption: templateOptions.source === "sent" ? "What the customer could select · shown as sent" : "Current template options",
                buttons: templateOptions.options,
              }}
              onClose={() => setShowTemplateOptions(false)}
            />
          </>
        )}

        {/* Inbound WhatsApp Flow submission — "Flow submitted" on its own
            tells the agent nothing the customer actually answered; this
            opens the same read-only preview modal with the real submitted
            values instead. */}
        {flowSubmission && flowSubmission.length > 0 && (
          <>
            <button
              onClick={() => setShowSubmission(true)}
              className="flex items-center gap-1.5 mt-1.5 text-xs font-semibold text-indigo-600 dark:text-indigo-400 underline bg-transparent border-none p-0 cursor-pointer"
            >
              <ListChecks size={13} /> View submission
            </button>
            <InteractiveOptionsModal
              isOpen={showSubmission}
              preview={{ kind: "flow_submission", headerText: "Flow Submission", fields: flowSubmission }}
              onClose={() => setShowSubmission(false)}
            />
          </>
        )}

        <div className="flex justify-end items-center gap-1 mt-1">
          <span className="text-[11px] text-slate-400 dark:text-gray-400">{displayTime}</span>
          {statusIcon}
        </div>
      </div>
    </div>
  );
};

interface RowProps { label: string; value: string; valueColor?: string; }
export const Row: React.FC<RowProps> = ({ label, value, valueColor }) => (
  <div className="flex justify-between py-1 text-xs">
    <span className="text-slate-500 dark:text-gray-400">{label}</span>
    <span
      className={`font-semibold ${valueColor ? "" : "text-slate-800 dark:text-gray-100"}`}
      style={valueColor ? { color: valueColor } : undefined}
    >
      {value}
    </span>
  </div>
);

export const GreenDot: React.FC<{ label: string }> = ({ label }) => (
  <div className="flex items-center gap-1.5 py-[3px]">
    <div className="w-2 h-2 rounded-full bg-green-500 flex-shrink-0" />
    <span className="text-xs text-slate-600 dark:text-gray-300">{label}</span>
  </div>
);

interface StepItemProps { label: string; done: boolean; active: boolean; }
export const StepItem: React.FC<StepItemProps> = ({ label, done, active }) => (
  <div className="flex items-center gap-2 py-1">
    <div className={`w-5 h-5 rounded-full flex-shrink-0 flex items-center justify-center ${done ? "bg-green-500" : active ? "bg-indigo-500 border-2 border-indigo-300" : "bg-slate-200 dark:bg-gray-700"
      }`}>
      {done && <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3"><path d="M20 6 9 17l-5-5" /></svg>}
    </div>
    <span className={`text-xs ${done ? "text-green-500 font-semibold" : active ? "text-indigo-500 font-semibold" : "text-slate-400 dark:text-gray-500"
      }`}>{label}</span>
  </div>
);


